import { initializePaddle, type Paddle } from '@paddle/paddle-js'
import { getAttribution, getFbp, getFbc } from './attribution'
import { getEnrollment } from './enrollment'
import { getVariant } from './variant'
import { getQaToken } from './qa'

export const OFFER_PAGE_VERSION = 'honest-v1'

let paddleInstance: Paddle | null = null

async function getPaddle(): Promise<Paddle> {
  if (paddleInstance) return paddleInstance

  const paddle = await initializePaddle({
    environment: import.meta.env.VITE_PADDLE_ENVIRONMENT === 'production' ? 'production' : 'sandbox',
    token: import.meta.env.VITE_PADDLE_CLIENT_TOKEN || '',
    eventCallback: (event) => {
      if (event.name === 'checkout.completed') {
        const customEvent = new CustomEvent('paddle:checkout.completed', { detail: event })
        window.dispatchEvent(customEvent)
      }
      if (event.name === 'checkout.closed') {
        const customEvent = new CustomEvent('paddle:checkout.closed', { detail: event })
        window.dispatchEvent(customEvent)
      }
    },
  })

  if (!paddle) {
    throw new Error('Failed to initialize Paddle')
  }

  paddleInstance = paddle
  return paddleInstance
}

export interface CheckoutOptions {
  priceId: string
  /** Funnel plan id ('1month' | '3month' | '1year') — forwarded as custom_data.plan_id. */
  planId?: string
  email: string
  /**
   * Browser-generated event ID for Meta Purchase dedup. Persisted into the
   * Paddle transaction's customData so the server-side CAPI fire can use it.
   */
  metaPurchaseEventId?: string
  onSuccess: (transactionId: string) => void
  onClose: () => void
}

export async function openCheckout({ priceId, planId, email, metaPurchaseEventId, onSuccess, onClose }: CheckoutOptions) {
  const paddle = await getPaddle()

  const handleSuccess = (e: Event) => {
    const customEvent = e as CustomEvent
    const transactionId = customEvent.detail?.data?.transaction_id || ''
    // Auto-close the Paddle checkout overlay so user sees the account creation screen
    setTimeout(() => {
      try { paddle.Checkout.close() } catch (_) { /* overlay may already be closed */ }
    }, 3000)
    onSuccess(transactionId)
    cleanup()
  }

  const handleClose = () => {
    onClose()
    cleanup()
  }

  const cleanup = () => {
    window.removeEventListener('paddle:checkout.completed', handleSuccess)
    window.removeEventListener('paddle:checkout.closed', handleClose)
  }

  window.addEventListener('paddle:checkout.completed', handleSuccess)
  window.addEventListener('paddle:checkout.closed', handleClose)

  const attribution = getAttribution()
  const customData: Record<string, string> = {}
  // Google click ids → overall/scripts/gads_kp_web_conversions.py uploads trials to Google Ads
  if (attribution?.gclid) customData.gclid = attribution.gclid
  if (attribution?.gbraid) customData.gbraid = attribution.gbraid
  if (attribution?.wbraid) customData.wbraid = attribution.wbraid
  if (attribution?.utm_source) customData.utm_source = attribution.utm_source
  if (attribution?.utm_campaign) customData.utm_campaign = attribution.utm_campaign

  if (attribution?.utm_medium) customData.utm_medium = attribution.utm_medium

  // Meta browser cookies + dedup event id + the browser's user agent,
  // forwarded via Paddle customData (copied onto the subscription) to the
  // server Meta CAPI outbox. meta_event_id is only ever used server-side for
  // the checkout's own event (trial start / immediate purchase). Client IP is
  // deliberately not captured.
  const fbp = getFbp()
  const fbc = getFbc()
  if (fbp) customData.fbp = fbp
  if (fbc) customData.fbc = fbc
  if (metaPurchaseEventId) customData.meta_event_id = metaPurchaseEventId
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    customData.client_user_agent = navigator.userAgent.slice(0, 512)
  }

  // Plan §A4: the browser's enrollment record rides on the purchase, so the
  // enrollment that counts is the one attached to the Paddle subscription.
  const enrollment = getEnrollment()
  customData.enrollment_id = enrollment.enrollment_id
  customData.first_seen_at = enrollment.first_seen_at
  customData.experiment_version = enrollment.experiment_version
  customData.enrollment_variant = enrollment.variant
  if (enrollment.utm_campaign) customData.enrollment_utm_campaign = enrollment.utm_campaign
  customData.variant = getVariant()
  if (planId) customData.plan_id = planId
  // Which offer-page copy the buyer saw, so Paddle reads can split before/after a change
  // (no field = the pre-2026-10 page with the fake timer and was-prices). Bump on every offer-copy change.
  customData.offer_page = OFFER_PAGE_VERSION

  // Plan §A5/§9.1: the signed QA token itself (never a boolean) — the server
  // re-verifies it before applying test_event_code / qa tagging.
  const qaToken = getQaToken()
  if (qaToken) customData.qa_token = qaToken

  paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    customer: { email },
    customData: Object.keys(customData).length > 0 ? customData : undefined,
    settings: {
      displayMode: 'overlay',
      theme: 'light',
      locale: 'en',
    },
  })
}

export function getPaddlePriceIds(): Record<string, string> {
  return {
    '1month': import.meta.env.VITE_PADDLE_PRICE_1MONTH || '',
    '3month': import.meta.env.VITE_PADDLE_PRICE_3MONTH || '',
    '1year': import.meta.env.VITE_PADDLE_PRICE_1YEAR || '',
  }
}
